import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { CandidateQueryDetail } from '@aegislab/shared';
import {
  candidateKeys,
  fetchCandidateQuery,
  isCandidateQuerySettled,
  useCandidateQueryWatchStore,
  type CandidateQueryWatch,
} from '@/entities/outreach-candidate';
import { useToastStore, type ToastItem } from '@/shared/lib/toast';

const POLL_MS = 5_000;
const WATCH_LIMIT_MS = 60 * 60_000;
const n = new Intl.NumberFormat('ko-KR');

/**
 * 조회가 끝난 순간의 알림 문구. 끝나지 않은 상태이거나 알릴 일이 아닌 `expired` 는 `null`.
 * 나라를 제목에 넣어 여러 나라가 잇달아 끝나도 어느 나라 결과인지 가려진다.
 */
export function candidateQueryNoticeOf(
  country: string,
  detail: Pick<CandidateQueryDetail, 'state' | 'totalCount'>
): Omit<ToastItem, 'id'> | null {
  if (detail.state === 'ready') {
    return {
      title: `${country} 후보 조회 완료`,
      body:
        detail.totalCount === null
          ? '메일 탭의 후보 선택에서 결과를 확인해 주세요.'
          : `조건에 맞는 후보 ${n.format(detail.totalCount)}명을 찾았습니다.`,
      type: 'success',
    };
  }
  if (detail.state === 'failed') {
    return {
      title: `${country} 후보 조회 실패`,
      body: '메일 탭의 후보 선택에서 다시 시도해 주세요.',
      type: 'error',
    };
  }
  return null;
}

/**
 * 조회 하나가 끝나면 나라를 담은 토스트를 한 번 띄우고 지켜보기를 거둔다. 조회 상태 질의는
 * 후보 선택 면과 같은 키라 같이 서 있어도 요청이 겹치지 않는다. 진행률은 다루지 않는다(LAB-27).
 */
export function useCandidateQueryCompletionNotice(entry: CandidateQueryWatch) {
  const addToast = useToastStore((st) => st.addToast);
  const unwatch = useCandidateQueryWatchStore((st) => st.unwatch);
  const detail = useQuery({
    queryKey: candidateKeys.query(entry.scenarioId, entry.queryId),
    queryFn: () => fetchCandidateQuery(entry.scenarioId, entry.queryId),
    // 상태 조회가 일시적으로 실패해도 멈추지 않는다 — 서버가 돌아오면 이어서 알린다.
    refetchInterval: (query) =>
      query.state.data && isCandidateQuerySettled(query.state.data.state) ? false : POLL_MS,
    staleTime: 0,
  });

  // 끝나지 않는 조회를 영영 붙들지 않는다 — 조회 수명(30일)보다 훨씬 짧게 거둔다.
  useEffect(() => {
    const timer = window.setTimeout(() => unwatch(entry.queryId), WATCH_LIMIT_MS);
    return () => window.clearTimeout(timer);
  }, [entry.queryId, unwatch]);

  useEffect(() => {
    if (!detail.data || !isCandidateQuerySettled(detail.data.state)) return;
    const notice = candidateQueryNoticeOf(entry.country, detail.data);
    if (notice) addToast(notice);
    unwatch(entry.queryId);
  }, [detail.data, entry.country, entry.queryId, addToast, unwatch]);
}
